import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../live/api.ts';
import { useStore } from '../live/context.tsx';
import { layoutPx, placeAnchoredMenu } from '../lib/anchored-menu.ts';
import { uiScale } from '../lib/display.ts';

/** The same rescan action on graph cards and ticket rows as on board cards. */
export function useTicketRescanMenu(ticketKey: string) {
  const store = useStore();
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const trigger = useRef<HTMLElement | null>(null);
  const close = () => { setPosition(null); trigger.current?.focus(); };
  useEffect(() => {
    if (!position) return;
    const dismiss = () => setPosition(null);
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.stopPropagation(); dismiss(); trigger.current?.focus(); }
    };
    window.addEventListener('keydown', escape);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [position]);
  const onContextMenu = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;
    trigger.current = event.currentTarget;
    const scale = uiScale();
    const x = layoutPx(event.clientX, scale);
    const y = layoutPx(event.clientY, scale);
    setPosition(placeAnchoredMenu({ anchor: { top: y, bottom: y, left: x, right: x },
      menuWidth: 224, menuHeight: 48,
      viewportWidth: layoutPx(window.innerWidth, scale), viewportHeight: layoutPx(window.innerHeight, scale) }));
  };
  const run = async () => {
    close();
    setBusy(true);
    try {
      const result = await api.rescanTicket(ticketKey);
      store.toast('good', result.message);
      await store.refetch();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally { setBusy(false); }
  };
  const menu = position ? createPortal(<>
    <div className="tcard-menu-scrim" role="presentation" onClick={(event) => { event.stopPropagation(); close(); }} />
    <div className="menu tcard-menu" role="menu" aria-label={`${ticketKey} actions`} style={position}
      onKeyDown={(event) => { event.stopPropagation(); if (event.key === 'Escape') close(); }}>
      <button type="button" role="menuitem" className="menu-item" autoFocus
        title="Regenerate footprint, then dependencies. Reset failed retries."
        onClick={(event) => { event.stopPropagation(); void run(); }}>Rescan</button>
    </div>
  </>, document.body) : null;
  return { onContextMenu, menu };
}
